import './orgChart.css';

/**
 * Presentational chrome. All state lives in OrgChartPage.
 *
 * The Status select groups employment statuses apart from planning labels, since
 * both are filterable but they mean different things. Options come from the
 * payload, never a hardcoded list: plan labels are free text.
 *
 * Controls are split into two groups so the CSS can space them apart and wrap
 * each group as a unit on a narrow viewport:
 *   - filters group: Search, Function, Organization, Status, Reset (Reset
 *     clears the filters, so it belongs with them) and the matched count.
 *   - view group: the View segmented control, the zoom stepper (tree only),
 *     Expand all and Collapse all, kept adjacent to each other and the zoom.
 * The freshness label sits outside both groups, at the end.
 *
 * Organization is a checkbox group (fieldset/legend), not a select: the
 * reader can tick any combination of the companies present in the payload.
 * `filters.org` is null when every available org is selected (the neutral,
 * no-narrowing state) or a Set of the selected org names otherwise.
 *
 * The admin-only "All companies (unfiltered)" override is a separate toggle,
 * deliberately NOT part of the Organization checkbox group: it does not
 * filter anything client-side, it widens the server query beyond the
 * admin-configured company scope, so folding it into the filter control let a
 * simple filter click accidentally trigger a refetch. Keeping it a distinct
 * control makes that impossible.
 */
export default function OrgChartToolbar({
  view, onView,
  functions, org, statuses,
  filters, onFilter, onOrgToggle,
  search, onSearch,
  matchedCount, anyFilter,
  zoom, onZoom,
  onExpandAll, onCollapseAll, onReset, onCenter,
  freshness,
  isAdmin, allCompanies, onAllCompanies,
  fullscreenSupported, isFullscreen, onFullscreen,
  onExport,
}) {
  return (
    <div className="oc-controls">
      <div className="oc-group oc-group--filters">
        <div className="oc-field oc-field--search">
          <label htmlFor="oc-search">Search</label>
          <input
            id="oc-search"
            type="search"
            className="search-input"
            placeholder="Search by name or job title"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
          />
        </div>

        <div className="oc-field">
          <label htmlFor="oc-func">Function</label>
          <select id="oc-func" className="oc-select" value={filters.func}
            onChange={(e) => onFilter('func', e.target.value)}>
            <option value="all">All functions</option>
            {functions.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>

        <fieldset className="oc-field oc-org-group">
          <legend>Organization</legend>
          <div className="oc-org-checks">
            {org.map((o) => (
              <label key={o} className="oc-org-check">
                <input
                  type="checkbox"
                  checked={filters.org === null || filters.org.has(o)}
                  onChange={() => onOrgToggle(o)}
                />
                {o}
              </label>
            ))}
          </div>
        </fieldset>

        {isAdmin && (
          <div
            className="oc-field oc-admin-toggle"
            title="Admin only. Loads companies outside the configured scope from the server."
          >
            {/* Carries the same label row as every other field so its box lines
                up with them. Not a `for`-linked label: the control's own text is
                inside the clickable .oc-org-check label below. */}
            <label aria-hidden="true">Scope</label>
            <label className="oc-org-check">
              <input
                type="checkbox"
                checked={allCompanies}
                onChange={(e) => onAllCompanies(e.target.checked)}
              />
              All companies
            </label>
            <p className="oc-admin-toggle-help">
              Admin only. Loads companies outside the configured scope from the server.
            </p>
          </div>
        )}

        <div className="oc-field">
          <label htmlFor="oc-status">Status</label>
          <select id="oc-status" className="oc-select" value={filters.status}
            onChange={(e) => onFilter('status', e.target.value)}>
            <option value="all">All statuses</option>
            {statuses.people.length > 0 && (
              <optgroup label="Employment">
                {statuses.people.map((s) => <option key={s} value={s}>{s}</option>)}
              </optgroup>
            )}
            {statuses.planned.length > 0 && (
              <optgroup label="Planned">
                {statuses.planned.map((s) => <option key={s} value={s}>{s}</option>)}
              </optgroup>
            )}
          </select>
        </div>

        <button type="button" className="btn btn-secondary" onClick={onReset}>Reset</button>

        {anyFilter && <span className="oc-freshness">{matchedCount} matched</span>}
      </div>

      <div className="oc-group oc-group--view">
        <div className="oc-field">
          <label id="oc-view-label">View</label>
          <div className="oc-seg" role="group" aria-labelledby="oc-view-label">
            <button type="button" className={view === 'tree' ? 'active' : ''} onClick={() => onView('tree')}>Tree</button>
            <button type="button" className={view === 'list' ? 'active' : ''} onClick={() => onView('list')}>List</button>
          </div>
        </div>

        {view === 'tree' && (
          <div className="oc-zoom">
            <button type="button" onClick={() => onZoom(-0.1)} aria-label="Zoom out">-</button>
            <span>{Math.round(zoom * 100)}%</span>
            <button type="button" onClick={() => onZoom(0.1)} aria-label="Zoom in">+</button>
          </div>
        )}

        <button type="button" className="btn btn-secondary" onClick={onExpandAll}>Expand all</button>
        <button type="button" className="btn btn-secondary" onClick={onCollapseAll}>Collapse all</button>

        {/* Recentres the tree's horizontal scroll, same as the initial-load and
            zoom/expand/collapse auto-centring OrgTreeView already does on its
            own. Tree view only, same restriction as the zoom stepper: the list
            view has no horizontal scroll to centre. */}
        {view === 'tree' && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCenter}
            title="Centre the tree in view"
          >
            Center
          </button>
        )}

        {/* Opens the export dialog rather than printing straight away: the
            reader has to choose a scope (this view, or one department), and
            the dialog is also where the "Save as PDF" destination and the
            background-graphics caveat get explained. */}
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onExport}
          title="Export the chart as a landscape PDF"
        >
          Export PDF
        </button>

        {/* Hidden rather than disabled when the Fullscreen API is unavailable
            (older Safari, some embedded webviews): a control that is always
            going to fail on this browser is worse than no control. */}
        {fullscreenSupported && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onFullscreen}
            title={isFullscreen ? 'Exit fullscreen' : 'View the chart fullscreen'}
          >
            {isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          </button>
        )}
      </div>

      {freshness && <span className="oc-freshness">Updated {freshness}</span>}
    </div>
  );
}
